import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface MatrixType {
  name: string;
  english: string;
  description: string;
  color: string;
}

interface KeyOperation {
  name: string;
  symbol: string;
  description: string;
  icon: string;
  color: string;
}

interface ApplicationField {
  name: string;
  english: string;
  icon: string;
}

interface LinearAlgebraTemplateProps {
  topic: string;
  subtitle: string;
  matrixTypes: MatrixType[];
  operations: KeyOperation[];
  applications: ApplicationField[];
  eigenvalueSteps: { step: string; description: string }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: LinearAlgebraTemplateProps = {
  topic: '线性代数与向量空间',
  subtitle: 'Linear Algebra & Vector Spaces',
  matrixTypes: [
    {
      name: '单位矩阵',
      english: 'Identity Matrix',
      description: '对角线全为1，其余为0，乘法中的"1"',
      color: '#818cf8',
    },
    {
      name: '对角矩阵',
      english: 'Diagonal Matrix',
      description: '仅对角线有非零元素，简化计算的利器',
      color: '#a78bfa',
    },
    {
      name: '稀疏矩阵',
      english: 'Sparse Matrix',
      description: '大部分元素为零，大规模计算的核心优化',
      color: '#c4b5fd',
    },
    {
      name: '正交矩阵',
      english: 'Orthogonal Matrix',
      description: '行列向量相互正交，旋转变换的数学表示',
      color: '#c7d2fe',
    },
  ],
  operations: [
    {
      name: '矩阵乘法',
      symbol: 'A×B',
      description: '线性变换的复合运算',
      icon: '✖️',
      color: '#818cf8',
    },
    {
      name: '行列式',
      symbol: 'det(A)',
      description: '衡量线性变换的缩放因子',
      icon: '🔢',
      color: '#a78bfa',
    },
    {
      name: '特征值',
      symbol: 'Ax=λx',
      description: '变换中保持方向不变的特殊标量',
      icon: '🎯',
      color: '#c4b5fd',
    },
    {
      name: 'SVD分解',
      symbol: 'UΣV*',
      description: '矩阵分解为旋转与缩放的组合',
      icon: '🧩',
      color: '#c7d2fe',
    },
  ],
  applications: [
    { name: '图形变换', english: '3D Graphics', icon: '🎮' },
    { name: '机器学习', english: 'Machine Learning', icon: '🤖' },
    { name: '量子计算', english: 'Quantum Computing', icon: '⚛️' },
    { name: '信号处理', english: 'Signal Processing', icon: '📡' },
  ],
  eigenvalueSteps: [
    { step: '构建矩阵', description: '将线性变换表示为矩阵形式' },
    { step: '求特征方程', description: 'det(A - λI) = 0' },
    { step: '求解特征值', description: '解特征多项式得到 λ 值' },
    { step: '求特征向量', description: '代入 (A - λI)x = 0 求解' },
  ],
  stats: [
    { value: 'n×n', label: '方阵是线性代数的核心' },
    { value: 'Ax=b', label: '线性方程组的矩阵形式' },
    { value: 'rank', label: '秩决定解空间的维度' },
  ],
  bottomQuote:
    '矩阵是现代科学的通用语言——从量子力学到人工智能，线性代数无处不在',
};

export const LinearAlgebraTemplate: React.FC<
  Partial<LinearAlgebraTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const matrixStart = 30;
  const operationStart = 160;
  const axesStart = 200;
  const appStart = 340;
  const eigenStart = 260;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const axesDraw = interpolate(
    frame,
    [axesStart, axesStart + 60],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  const vectorRotate = interpolate(
    frame,
    [axesStart + 60, axesStart + 200],
    [0, 45],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #1e1b4b 0%, #0c0a2a 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Grid-like floating dot particles */}
      {Array.from({ length: 60 }).map((_, i) => {
        const col = i % 10;
        const row = Math.floor(i / 10);
        const baseX = col * 200 + 60;
        const baseY = row * 180 + 50;
        const drift = Math.sin(frame * 0.02 + i * 0.5) * 5;
        const pulse = 0.05 + 0.04 * Math.sin(frame * 0.035 + i * 0.6);
        const colors = ['#818cf8', '#a78bfa', '#c4b5fd', '#c7d2fe'];
        return (
          <div
            key={`p-${i}`}
            style={{
              position: 'absolute',
              left: baseX,
              top: baseY + drift,
              width: 3,
              height: 3,
              borderRadius: '50%',
              background: colors[i % colors.length],
              opacity: pulse,
              boxShadow: `0 0 8px ${colors[i % colors.length]}30`,
            }}
          />
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#4338ca',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#c7d2fe',
            flexShrink: 0,
          }}
        >
          🔢 线性代数
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#e0e7ff',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 26,
            color: '#94a3b8',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Matrix Types ===== */}
      <div style={{ position: 'absolute', top: 120, left: 36, width: 500 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          矩阵类型
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.matrixTypes.map((mt, i) => {
            const s = spring({
              frame: frame - (matrixStart + i * 16),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  background: '#1a1744',
                  borderRadius: 14,
                  padding: '14px 18px',
                  border: `1.5px solid ${mt.color}30`,
                  opacity: s,
                  transform: `translateX(${(1 - s) * 30}px)`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                  <span style={{ fontSize: 22, fontWeight: 800, color: mt.color }}>
                    {mt.name}
                  </span>
                  <span style={{ fontSize: 20, color: '#64748b' }}>
                    {mt.english}
                  </span>
                </div>
                <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 4 }}>
                  {mt.description}
                </div>
              </div>
            );
          })}
        </div>

        {/* Key Operations */}
        <div style={{ marginTop: 20 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            核心运算
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {p.operations.map((op, i) => {
              const s = spring({
                frame: frame - (operationStart + i * 16),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    minWidth: 0,
                    background: '#1a1744',
                    borderRadius: 12,
                    padding: '10px 12px',
                    border: `1px solid ${op.color}30`,
                    opacity: s,
                    transform: `translateX(${(1 - s) * 24}px)`,
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      background: `${op.color}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 22,
                      flexShrink: 0,
                    }}
                  >
                    {op.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                      <span style={{ fontSize: 22, fontWeight: 800, color: op.color }}>
                        {op.name}
                      </span>
                      <span
                        style={{
                          fontSize: 20,
                          color: '#c7d2fe',
                          fontFamily: 'monospace',
                        }}
                      >
                        {op.symbol}
                      </span>
                    </div>
                    <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 2 }}>
                      {op.description}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== CENTER: 3D Coordinate Axes SVG ===== */}
      <div style={{ position: 'absolute', top: 120, left: 650, width: 720 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          三维坐标空间 · 向量变换
        </div>
        <svg width="700" height="400" viewBox="0 0 560 320" overflow="visible">
          <defs>
            <marker id="arrowX" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
              <polygon points="0 0, 8 3, 0 6" fill="#818cf8" />
            </marker>
            <marker id="arrowY" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
              <polygon points="0 0, 8 3, 0 6" fill="#a78bfa" />
            </marker>
            <marker id="arrowZ" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
              <polygon points="0 0, 8 3, 0 6" fill="#c4b5fd" />
            </marker>
          </defs>

          {/* Grid lines */}
          {[-2, -1, 1, 2].map((n) => (
            <g key={`grid-${n}`} opacity={0.15 * axesDraw}>
              <line
                x1={280 + n * 50} y1="40" x2={280 + n * 50} y2="280"
                stroke="#818cf8" strokeWidth="0.5"
              />
              <line
                x1="80" y1={160 + n * 50} x2="480" y2={160 + n * 50}
                stroke="#818cf8" strokeWidth="0.5"
              />
            </g>
          ))}

          {/* X-axis */}
          <line
            x1="80" y1="160" x2={80 + 400 * axesDraw} y2="160"
            stroke="#818cf8" strokeWidth="2.5"
            markerEnd="url(#arrowX)"
          />
          <text
            x={490} y="165" fill="#818cf8" fontSize="18" fontWeight="700"
            opacity={axesDraw}
          >
            x
          </text>

          {/* Y-axis */}
          <line
            x1="280" y1="280" x2="280" y2={280 - 240 * axesDraw}
            stroke="#a78bfa" strokeWidth="2.5"
            markerEnd="url(#arrowY)"
          />
          <text
            x="286" y="30" fill="#a78bfa" fontSize="18" fontWeight="700"
            opacity={axesDraw}
          >
            y
          </text>

          {/* Z-axis (perspective) */}
          <line
            x1="280" y1="160"
            x2={280 - 120 * axesDraw} y2={160 + 120 * axesDraw}
            stroke="#c4b5fd" strokeWidth="2"
            markerEnd="url(#arrowZ)"
            strokeDasharray="4 3"
          />
          <text
            x="148" y="295" fill="#c4b5fd" fontSize="18" fontWeight="700"
            opacity={axesDraw}
          >
            z
          </text>

          {/* Animated vector */}
          <g
            opacity={interpolate(
              frame,
              [axesStart + 50, axesStart + 70],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            )}
            transform={`rotate(${vectorRotate}, 280, 160)`}
          >
            <line
              x1="280" y1="160" x2="420" y2="80"
              stroke="#f59e0b" strokeWidth="3"
            />
            <circle cx="420" cy="80" r="5" fill="#f59e0b" />
            <text x="428" y="78" fill="#f59e0b" fontSize="14" fontWeight="700">
              v⃗
            </text>
          </g>

          {/* Transformed vector */}
          <g
            opacity={interpolate(
              frame,
              [axesStart + 80, axesStart + 100],
              [0, 0.5],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            )}
            transform={`rotate(${vectorRotate + 30}, 280, 160)`}
          >
            <line
              x1="280" y1="160" x2="400" y2="90"
              stroke="#f87171" strokeWidth="2"
              strokeDasharray="6 3"
            />
            <circle cx="400" cy="90" r="4" fill="#f87171" />
            <text x="408" y="88" fill="#f87171" fontSize="13">
              Av⃗
            </text>
          </g>

          {/* Origin label */}
          <text x="268" y="178" fill="#64748b" fontSize="13">O</text>

          {/* Matrix notation */}
          <g
            transform="translate(40, 32)"
            opacity={interpolate(
              frame,
              [axesStart + 100, axesStart + 120],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            )}
          >
            <rect x="380" y="240" width="160" height="60" rx="8" fill="#1a174480" stroke="#4f46e530" strokeWidth="1" />
            <text x="400" y="264" fill="#c7d2fe" fontSize="14" fontFamily="monospace">
              A = [a₁₁ a₁₂]
            </text>
            <text x="420" y="286" fill="#c7d2fe" fontSize="14" fontFamily="monospace">
              [a₂₁ a₂₂]
            </text>
          </g>
        </svg>

        {/* Eigenvalue Steps */}
        <div style={{ marginTop: 14 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            特征值求解流程
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {p.eigenvalueSteps.map((es, i) => {
              const s = spring({
                frame: frame - (eigenStart + i * 18),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    background: '#1a1744',
                    borderRadius: 12,
                    padding: '12px 10px',
                    textAlign: 'center',
                    border: '1px solid #312e81',
                    opacity: s,
                    transform: `translateY(${(1 - s) * 16}px)`,
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: '#4f46e5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: 900,
                      color: '#e0e7ff',
                      margin: '0 auto 6px',
                    }}
                  >
                    {i + 1}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#c7d2fe' }}>
                    {es.step}
                  </div>
                  <div style={{ fontSize: 20, color: '#64748b', marginTop: 4, fontFamily: es.description.includes('=') ? 'monospace' : 'inherit' }}>
                    {es.description}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== RIGHT COLUMN: Applications ===== */}
      <div style={{ position: 'absolute', top: 120, right: 36, width: 340 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          应用领域
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {p.applications.map((app, i) => {
            const s = spring({
              frame: frame - (appStart + i * 16),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  background: '#1a1744',
                  borderRadius: 14,
                  padding: '14px 16px',
                  border: '1.5px solid #312e81',
                  opacity: s,
                  transform: `translateX(${(1 - s) * 24}px)`,
                }}
              >
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    background: '#4f46e520',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 28,
                    flexShrink: 0,
                    boxShadow: '0 0 18px #4f46e530',
                  }}
                >
                  {app.icon}
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#c7d2fe' }}>
                    {app.name}
                  </div>
                  <div style={{ fontSize: 20, color: '#64748b', marginTop: 2 }}>
                    {app.english}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Matrix visual */}
        <div style={{ marginTop: 24 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 12,
              letterSpacing: '0.08em',
            }}
          >
            矩阵可视化
          </div>
          <svg width="340" height="250" viewBox="0 0 340 250">
            {/* 4x4 grid matrix visualization */}
            {Array.from({ length: 16 }).map((_, idx) => {
              const row = Math.floor(idx / 4);
              const col = idx % 4;
              const cellX = 36 + col * 70;
              const cellY = 12 + row * 50;
              const isDiag = row === col;
              const val = isDiag ? '1' : '0';
              const cellIn = spring({
                frame: frame - (appStart + 70 + idx * 4),
                fps,
                config: { damping: 14, stiffness: 140 },
              });
              return (
                <g key={idx} opacity={cellIn}>
                  <rect
                    x={cellX} y={cellY} width="62" height="44" rx="8"
                    fill={isDiag ? '#4f46e530' : '#1a174480'}
                    stroke={isDiag ? '#818cf8' : '#312e8150'}
                    strokeWidth={isDiag ? 2 : 0.6}
                  />
                  <text
                    x={cellX + 31} y={cellY + 30}
                    fill={isDiag ? '#818cf8' : '#475569'}
                    fontSize="24" fontWeight={isDiag ? '800' : '400'}
                    textAnchor="middle" fontFamily="monospace"
                  >
                    {val}
                  </text>
                </g>
              );
            })}
            <text x="170" y="240" fill="#64748b" fontSize="22" textAnchor="middle">
              I₄ · 四阶单位矩阵
            </text>
          </svg>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#1a1744',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #312e81',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    fontSize: 30,
                    fontWeight: 900,
                    color: '#818cf8',
                    fontFamily: 'monospace',
                  }}
                >
                  {st.value}
                </span>
                <span style={{ fontSize: 20, color: '#94a3b8' }}>{st.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 36,
          right: 36,
          background: '#1a1744',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #4338ca',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>🧮</span>
          <span style={{ fontSize: 20, color: '#e0e7ff', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #4f46e5, #818cf8, #c7d2fe)',
        }}
      />
    </AbsoluteFill>
  );
};
